// Updated AdminWorkEdit.jsx with gallery upload buttons

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminHeader from '../../components/AdminHeader';
import { getWorkItem, updateWorkItem, uploadImage, uploadVideo } from '../../services/api';
import { toast } from 'sonner';
import { Upload, Link as LinkIcon, Loader, Save, ArrowLeft, FileText, Image as ImageIcon, Video, FolderOpen } from 'lucide-react';

const AdminWorkEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    title: '',
    role: '',
    image_url: '',
    border_color: '#ff6b6b',
    video_url: '',
    description: '',
    order: 0,
    additional_images: [],
    vimeo_embeds: [],
    show_image: true,
    show_video: true
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingGalleryImages, setUploadingGalleryImages] = useState(false);
  const [uploadingGalleryVideos, setUploadingGalleryVideos] = useState(false);
  const [galleryUploadProgress, setGalleryUploadProgress] = useState(0);

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FileText },
    { id: 'media', label: 'Primary Media', icon: ImageIcon },
    { id: 'gallery', label: 'Gallery', icon: FolderOpen },
    { id: 'details', label: 'Details', icon: Video }
  ];

  useEffect(() => {
    fetchWorkItem();
  }, [id]);

  const fetchWorkItem = async () => {
    try {
      const data = await getWorkItem(id);
      setFormData({
        title: data.title || '',
        role: data.role || '',
        image_url: data.image_url || '',
        border_color: data.border_color || '#ff6b6b',
        video_url: data.video_url || '',
        description: data.description || '',
        order: data.order || 0,
        additional_images: data.additional_images || [],
        vimeo_embeds: data.vimeo_embeds || [],
        show_image: data.show_image !== false,
        show_video: data.show_video !== false
      });
    } catch (error) {
      console.error('Error fetching work item:', error);
      toast.error('Failed to load work item');
      navigate('/admin/work');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadImage(file);
      setFormData({ ...formData, image_url: response.file_url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingVideo(true);
    try {
      const response = await uploadVideo(file, (progress) => {
        setUploadProgress(progress);
      });
      setFormData({ ...formData, video_url: response.file_url });
      toast.success('Video uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload video');
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
    }
  };

  const handleGalleryImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingGalleryImages(true);
    try {
      const newUrls = [];
      for (let i = 0; i < files.length; i++) {
        const response = await uploadImage(files[i]);
        newUrls.push(response.file_url);
        setGalleryUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }
      setFormData({
        ...formData,
        additional_images: [...formData.additional_images, ...newUrls]
      });
      toast.success(`${files.length} image(s) uploaded successfully!`);
    } catch (error) {
      console.error('Gallery image upload error:', error);
      toast.error('Failed to upload gallery images');
    } finally {
      setUploadingGalleryImages(false);
      setGalleryUploadProgress(0);
    }
  };

  const handleGalleryVideoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingGalleryVideos(true);
    try {
      const newUrls = [];
      for (let i = 0; i < files.length; i++) {
        const response = await uploadVideo(files[i], (progress) => {
          setGalleryUploadProgress(Math.round(((i + 1) / files.length) * progress / 100 * 100));
        });
        newUrls.push(response.file_url);
      }
      setFormData({
        ...formData,
        vimeo_embeds: [...formData.vimeo_embeds, ...newUrls]
      });
      toast.success(`${files.length} video(s) uploaded successfully!`);
    } catch (error) {
      console.error('Gallery video upload error:', error);
      toast.error('Failed to upload gallery videos');
    } finally {
      setUploadingGalleryVideos(false);
      setGalleryUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await updateWorkItem(id, formData);
      toast.success('Work item updated successfully!');
      navigate('/admin/work');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to update work item');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 text-lg">Loading project...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f]">
      <AdminHeader />

      <main className="max-w-[1000px] mx-auto px-8 py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">BACK</span>
        </button>

        <h1 className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-purple-600 font-['Poppins'] text-5xl font-black mb-8">
          EDIT PROJECT
        </h1>

        <div className="glass-effect rounded-2xl border border-white/10 overflow-hidden">
          {/* Tab Navigation */}
          <div className="flex border-b border-white/10 bg-white/5">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-6 py-4 flex items-center justify-center gap-2 font-semibold transition-all relative ${
                    activeTab === tab.id
                      ? 'text-white bg-white/10'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon size={20} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500" />
                  )}
                </button>
              );
            })}
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* BASIC INFO TAB */}
            {activeTab === 'basic' && (
              <div className="space-y-6 animate-fadeIn">
                <h2 className="text-2xl font-bold text-white font-['Poppins'] mb-6">Basic Information</h2>
                
                {/* Title */}
                <div>
                  <label className="block text-white font-['Poppins'] text-lg font-semibold mb-2">
                    PROJECT TITLE *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="e.g., 2023 SHOWREEL"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all text-lg"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-white font-['Poppins'] text-lg font-semibold mb-2">
                    YOUR ROLE *
                  </label>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    placeholder="e.g., DIRECTOR."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all text-lg"
                  />
                </div>

                {/* Border Color */}
                <div>
                  <label className="block text-white font-['Poppins'] text-lg font-semibold mb-2">
                    BORDER COLOR
                  </label>
                  <div className="flex gap-4 items-center">
                    <input
                      type="color"
                      name="border_color"
                      value={formData.border_color}
                      onChange={handleChange}
                      className="h-12 w-24 cursor-pointer rounded-lg"
                    />
                    <input
                      type="text"
                      value={formData.border_color}
                      onChange={(e) => setFormData({ ...formData, border_color: e.target.value })}
                      className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Order */}
                <div>
                  <label className="block text-white font-['Poppins'] text-lg font-semibold mb-2">
                    DISPLAY ORDER
                  </label>
                  <input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-lg"
                    min="0"
                  />
                  <p className="text-sm text-gray-400 mt-2">Lower numbers appear first</p>
                </div>
              </div>
            )}

            {/* PRIMARY MEDIA TAB */}
            {activeTab === 'media' && (
              <div className="space-y-6 animate-fadeIn">
                <h2 className="text-2xl font-bold text-white font-['Poppins'] mb-6">Primary Media</h2>
                
                {/* Cover Image */}
                <div>
                  <label className="block text-white font-['Poppins'] text-lg font-semibold mb-2">
                    COVER IMAGE *
                  </label>
                  {formData.image_url && (
                    <div className="mb-4">
                      <img 
                        src={formData.image_url} 
                        alt="Current cover"
                        className="w-full h-48 object-cover rounded-lg border border-white/10"
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                    disabled={uploading}
                  />
                  <label
                    htmlFor="image-upload"
                    className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg cursor-pointer hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 font-medium shadow-lg shadow-purple-500/30"
                  >
                    {uploading ? 'UPLOADING...' : 'SELECT IMAGE'}
                  </label>
                  <div className="flex items-center gap-2 mt-4">
                    <input
                      type="checkbox"
                      id="show_image"
                      checked={formData.show_image}
                      onChange={(e) => setFormData({ ...formData, show_image: e.target.checked })}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-purple-500 focus:ring-purple-500"
                    />
                    <label htmlFor="show_image" className="text-gray-300 text-sm">
                      Show cover image on detail page
                    </label>
                  </div>
                </div>

                {/* Project Video */}
                <div>
                  <label className="block text-white font-['Poppins'] text-lg font-semibold mb-2">
                    PROJECT VIDEO
                  </label>
                  {formData.video_url && (
                    <div className="mb-4">
                      <video 
                        src={formData.video_url} 
                        controls
                        className="w-full h-48 object-cover rounded-lg border border-white/10"
                      />
                    </div>
                  )}
                  {uploadingVideo && (
                    <div className="w-full bg-white/10 h-2 mb-4 rounded-full overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                    id="video-upload"
                    disabled={uploadingVideo}
                  />
                  <label
                    htmlFor="video-upload"
                    className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg cursor-pointer hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 font-medium shadow-lg shadow-purple-500/30"
                  >
                    {uploadingVideo ? `UPLOADING... ${uploadProgress}%` : 'SELECT VIDEO'}
                  </label>
                  {/* YouTube/Vimeo Link */}
                  <p className="text-sm text-gray-400 mt-4 mb-2 flex items-center gap-2">
                    <LinkIcon size={16} />
                    Or paste YouTube/Vimeo embed URL:
                  </p>
                  <input
                    type="url"
                    name="video_url"
                    value={formData.video_url}
                    onChange={handleChange}
                    placeholder="https://player.vimeo.com/video/..."
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all"
                  />
                  <div className="flex items-center gap-2 mt-4">
                    <input
                      type="checkbox"
                      id="show_video"
                      checked={formData.show_video}
                      onChange={(e) => setFormData({ ...formData, show_video: e.target.checked })}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-purple-500 focus:ring-purple-500"
                    />
                    <label htmlFor="show_video" className="text-gray-300 text-sm">
                      Show project video on detail page
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* GALLERY TAB */}
            {activeTab === 'gallery' && (
              <div className="space-y-6 animate-fadeIn">
                <h2 className="text-2xl font-bold text-white font-['Poppins'] mb-6">Additional Gallery</h2>
                
                {/* Additional Images */}
                <div>
                  <label className="block text-white font-['Poppins'] text-lg font-semibold mb-2">
                    ADDITIONAL IMAGES (OPTIONAL)
                  </label>
                  <p className="text-sm text-gray-400 mb-2">Add multiple image URLs, one per line</p>
                  <textarea
                    value={formData.additional_images.join('\n')}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      additional_images: e.target.value.split('\n').filter(url => url.trim())
                    })}
                    rows="6"
                    placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all resize-none font-mono text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-2">
                    <span className="bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                      {formData.additional_images.length} image(s)
                    </span>
                    These images will appear in a carousel on the project detail page
                  </p>
                  <div className="mt-4">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleGalleryImageUpload}
                      className="hidden"
                      id="gallery-image-upload"
                      disabled={uploadingGalleryImages}
                    />
                    <label
                      htmlFor="gallery-image-upload"
                      className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg cursor-pointer hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 font-medium shadow-lg shadow-purple-500/30"
                    >
                      {uploadingGalleryImages ? `UPLOADING... ${galleryUploadProgress}%` : 'UPLOAD IMAGES'}
                    </label>
                  </div>
                </div>

                {/* Vimeo Embeds / Additional Videos */}
                <div>
                  <label className="block text-white font-['Poppins'] text-lg font-semibold mb-2">
                    ADDITIONAL VIDEOS (OPTIONAL)
                  </label>
                  <p className="text-sm text-gray-400 mb-2">Add video URLs, one per line</p>
                  <textarea
                    value={formData.vimeo_embeds.join('\n')}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      vimeo_embeds: e.target.value.split('\n').filter(url => url.trim())
                    })}
                    rows="6"
                    placeholder="https://player.vimeo.com/video/123456&#10;https://player.vimeo.com/video/789012"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all resize-none font-mono text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-2 flex items-center gap-2">
                    <span className="bg-pink-500/20 text-pink-300 px-2 py-1 rounded">
                      {formData.vimeo_embeds.length} video(s)
                    </span>
                    These videos will appear in a carousel on the project detail page
                  </p>
                  <div className="mt-4">
                    <input
                      type="file"
                      accept="video/*"
                      multiple
                      onChange={handleGalleryVideoUpload}
                      className="hidden"
                      id="gallery-video-upload"
                      disabled={uploadingGalleryVideos}
                    />
                    <label
                      htmlFor="gallery-video-upload"
                      className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg cursor-pointer hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 font-medium shadow-lg shadow-purple-500/30"
                    >
                      {uploadingGalleryVideos ? `UPLOADING... ${galleryUploadProgress}%` : 'UPLOAD VIDEOS'}
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* DETAILS TAB */}
            {activeTab === 'details' && (
              <div className="space-y-6 animate-fadeIn">
                <h2 className="text-2xl font-bold text-white font-['Poppins'] mb-6">
                  Project Details
                </h2>
                
                {/* Description */}
                <div>
                  <label className="block text-white font-['Poppins'] text-lg font-semibold mb-2">
                    PROJECT DESCRIPTION (OPTIONAL)
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="8"
                    placeholder="Describe your project in detail...&#10;&#10;Share the story, challenges, and creative process behind this work."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all text-lg resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    {formData.description.length} characters
                  </p>
                </div>
              </div>
            )}

            {/* Submit Buttons - Fixed at bottom */}
            <div className="flex gap-4 pt-6 border-t border-white/10">
              <button
                type="submit"
                disabled={submitting || uploading || uploadingVideo || uploadingGalleryImages || uploadingGalleryVideos}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-purple-500/30"
              >
                {submitting ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    SAVING...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    SAVE CHANGES
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/work')}
                className="px-6 py-3 bg-white/5 border border-white/10 text-gray-300 rounded-lg hover:bg-white/10 transition-all"
              >
                CANCEL
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AdminWorkEdit;